<template>
  <v-card class="mb-3">
    <!-- HEADER -->
    <v-toolbar
      flat
      dense
      :color="
        $vuetify.theme.dark ? 'blue-grey darken-4' : 'blue-grey lighten-5'
      "
    >
      <v-toolbar-title class="text-subtitle-2">
        Cost Comparison
      </v-toolbar-title>

      <v-spacer />

      <v-btn x-small text :loading="loading" @click="getData">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
      <v-btn x-small text :disabled="!items.length" @click="exportCsv">
        <v-icon>mdi-download</v-icon>
      </v-btn>
    </v-toolbar>

    <!-- FILTERS -->
    <v-card-text class="py-3">
      <v-row dense>
        <v-col cols="12" sm="6" md="4">
          <!-- AUTOCOMPLETE STOCK FILTER -->
          <v-autocomplete
            v-model="selectedStock"
            :items="stockOptions"
            :loading="stockLoading"
            :search-input.sync="searchStock"
            dense
            clearable
            hide-details="auto"
            label="Search Stock"
            prepend-inner-icon="mdi-magnify"
            small-chips
            multiple
            item-text="label"
            item-value="stockId"
            @change="getData"
          />
        </v-col>
      </v-row>
    </v-card-text>

    <!-- TABLE -->
    <v-card-text>
      <v-skeleton-loader v-if="loading" type="table" class="mx-2 my-2" />

      <v-data-table
        v-else-if="items.length"
        item-key="stockId"
        group-by="stockLabel"
        disable-pagination
        :headers="headers"
        :items="items"
        hide-default-footer
        dense
      >
        <template #item.cost="{ value }">
          <td class="text-right">
            {{ Number(value).phpFormat() }}
          </td>
        </template>
        <template #item.supplier="{ item }">
          <td>
            {{ item.supplier }} <b>({{ item.supplierId }})</b>
          </td>
        </template>
      </v-data-table>

      <!-- EMPTY -->
      <div v-else class="py-8 text-center grey--text">
        <v-icon x-large class="mb-2">
          mdi-cash-remove
        </v-icon>
        <div>No supplier cost data available.</div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import appAxios from '@/extra-functions/axios.extra'
import { errorHandler } from '@/extra-functions/store.extra'
import searchDelay from '@/extra-functions/searchDelay.extra'
import { mapState } from 'vuex'

const stockSearchDelay = searchDelay()
const getDataSearchDelay = searchDelay()

export default {
  name: 'StockCostCompare',

  data() {
    return {
      loading: false,
      items: [],
      headers: [
        { text: 'Supplier', value: 'supplier' },
        { text: 'Cost', value: 'cost' },
        { text: 'Label', value: 'stockLabel', align: 'right' },
      ],
      /* Stock autocomplete */
      stockOptions: [],
      searchStock: '',
      selectedStock: [],
      stockLoading: false,
    }
  },

  computed: {
    ...mapState({
      token: (state) => state.auth.token,
    }),
  },

  watch: {
    // /* User types search text for stock */
    searchStock(val) {
      stockSearchDelay(() => {
        this.fetchStockOptions(val)
      })
    },

    // stockOptions(val) {
    //   console.log('options', val)
    // },

    // /* When a stock is selected from dropdown */
    selectedStock(val) {
      if (!val) {
        this.items = []
        return
      }
      this.getData()
    },
  },

  methods: {
    async fetchStockOptions(search) {
      if (!search) return

      try {
        this.stockLoading = true
        const { data } = await appAxios(this.token).get(
          '/v2/stock-suppliers/dropdown',
          {
            params: { search: this.searchStock },
          },
        )
        this.stockOptions = data.data.map((item) => ({
          ...item,
          label: `${item.productName} (${item.stockCode})`,
        }))
      } catch (e) {
        console.error(e)
        errorHandler(e)
      } finally {
        this.stockLoading = false
      }
    },

    getData() {
      getDataSearchDelay(async () => {
        try {
          if (!this.selectedStock.length) return
          this.loading = true
          const { data } = await appAxios(this.token).get(
            '/v2/stock-suppliers/stock-cost-compare',
            {
              params: {
                stocks: this.selectedStock,
              },
            },
          )
          console.log(data)
          this.items = data.data || []
        } catch (e) {
          console.error(e)
          errorHandler(e)
        } finally {
          this.loading = false
        }
      })
    },
    exportCsv() {
      if (!this.items.length) return

      const headers = ['Supplier', 'Cost']
      let currentGroup = null
      const rows = this.items.reduce((prev, next) => {
        const supplier = `${next.supplier} (${next.supplierId})`
        const cost = `${Number(next.cost).phpFormat()}`
        if (currentGroup !== next.stockLabel) {
          prev.push(next.stockLabel)
          currentGroup = next.stockLabel
        }
        prev.push([`"${supplier}"`, `"${cost}"`].join(','))
        return prev
      }, [])

      // Add UTF-8 BOM (so Excel shows ₱ correctly)
      const csvContent = '\uFEFF' + [headers.join(','), ...rows].join('\n')

      const blob = new Blob([csvContent], {
        type: 'text/csv;charset=utf-8;',
      })

      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `cost-comparison-${Date.now()}.csv`
      link.click()
      URL.revokeObjectURL(link.href)
    },
  },
}
</script>

<style scoped lang="scss">
.border-right {
  border-right: 1px solid rgba(128, 128, 128, 0.2);
}

.ellipsis {
  max-width: 260px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

thead th {
  background-color: rgba(0, 0, 0, 0.03);
  position: sticky;
  top: 0;
  z-index: 2;
}
</style>
